/**
 * UYAP hata yönlendirmeleri — merkezi sözlük (registry).
 *
 * Amaç: UYAP'tan dönen ham/teknik hataları tek bir yerden tanıyıp, kullanıcıya
 * ham metin yerine yönlendirici mesaj göstermek.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  YENİ HATA EKLEMEK İÇİN: Yalnızca aşağıdaki UYAP_ERROR_YONLENDIRMELERI
 *  dizisine bir kural nesnesi ekleyin. Çağıran kod (ör. tevziAl() catch blokları)
 *  değişmez; matchUyapError() otomatik olarak yeni kuralı da dener.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * İlgili: DEV-1902 / TDT-2707.
 */

export interface UyapErrorYonlendirme {
    /** Tanımlayıcı anahtar (loglama/test amaçlı; kullanıcıya gösterilmez). */
    key: string;
    /**
     * UYAP hata kodları. Hatanın errorCode'u (veya response.data.errorCode)
     * bu listedeyse kural eşleşir.
     */
    errorCodes: string[];
    /** Kullanıcıya gösterilecek yönlendirici mesaj. */
    userMessage: string;
}

/**
 * Yönlendirme sözlüğü. Kurallar sırayla denenir; ilk eşleşen kural döner.
 */
export const UYAP_ERROR_YONLENDIRMELERI: UyapErrorYonlendirme[] = [
    {
        key: 'TAMAMLANMAYAN_DOSYA',
        // TDT-2707 / DEV-1902 — UYAP "Tamamlanmayan Dosyalar" bekleyen kayıt varsa TevziAl reddi.
        errorCodes: ['PRTL_AVKT_10105'],
        userMessage:
            'Takip açılamadı — UYAP hesabınızın "Tamamlanmayan Dosyalar" bölümünde ' +
            'bekleyen kayıt bulunduğu için UYAP yeni takip açılmasına izin vermiyor. ' +
            'Bu, e-İcraPro kaynaklı bir sorun değildir; uyarı doğrudan UYAP tarafından gönderilmektedir.\n\n' +
            'Ne yapmalısınız:\n' +
            '1) UYAP Avukat Portalı > İcra > "Tamamlanmayan Dosyalar" ekranını açın.\n' +
            '2) Bekleyen dosyaları tamamlayın (aç/ödeme yap) veya iptal edin.\n' +
            '3) Yeni oluşan bir kayıt UYAP tarafından ilk 10 dakika silinemez; bu durumda kısa süre bekleyip tekrar deneyin.\n' +
            '4) Bölüm boşaldıktan sonra "Tevzi Al" işlemini yeniden başlatın.',
    },
    {
        key: 'ADRES_LIMITI',
        // Kişi/kurum için tanımlanabilecek maksimum adres sayısına ulaşıldı.
        errorCodes: ['SYS_GNL_10274'],
        userMessage:
            'UYAP tarafında kişi/kurum için tanımlanabilecek maksimum adres sayısına ulaşıldığı için ' +
            'yeni adres eklenememektedir. İşleme devam edebilmek için Detay ekranından UYAP kaynaklı ' +
            'mevcut bir adres seçerek devam ediniz.',
    },
    {
        key: 'EVRAK_GONDERME_AKSAKLIK',
        // davaAcilisEvrakGonderme_brd.ajx — UYAP kaynaklı geçici evrak kaydetme aksaklığı.
        errorCodes: ['PRTL_GNL_100044'],
        userMessage:
            'UYAP sistemlerinde yaşanan aksaklık sebebiyle işlemlerinizi daha sonra tekrar deneyebilirsiniz.',
    },
    {
        key: 'BAROKART_BAKIYE_YETERSIZ',
        // davaAcilisOdemeIslemleri_brd.ajx — Barokart (e-barobirlik) bakiyesi yetersiz.
        errorCodes: ['PRTL_GNL_100025-1'],
        userMessage:
            'Barokart (e-barobirlik) hesabınızda yeterli bakiye bulunmadığından ödeme işlemi tamamlanamadı.\n ' +
            'Tevzi işleminiz alınmış olabileceğinden, UYAP Portal üzerinden ' +
            '"Tamamlanmayan Dosyalar / İcra Takip Tamamlanmayan Dosyalar" bölümüne giderek ilgili dosyanızda ' +
            'ödemenin gerçekleşip gerçekleşmediğini kontrol ediniz. Ödeme yapılmamışsa, işleminizi aynı bölümden ' +
            'Vakıfbank ile tamamlayabilirsiniz.',
    },
    {
        key: 'ADRES_IL_ILCE_EKSIK',
        // icra_takip_tevzi_islemleri.ajx — UYAP/XML yazışma adresi il/ilçe bilgisi eksik.
        errorCodes: ['YRG_TRF_10126'],
        userMessage:
            'UYAP\'taki veya XML\'deki adres bilgilerinde (il/ilçe) eksiklik bulunduğu için tevzi işlemi tamamlanamadı.\n ' +
            'İlgili adresi, bu dosyanın sağ tarafındaki "Ön İzleme" butonuna tıklayarak açılan ekrandan düzeltip ' +
            'işlemi yeniden deneyebilirsiniz.',
    },
    {
        key: 'TAKIP_TALEBI_ICERIK_RET',
        // DEV-3880 — davaAcilisEvrakGonderme_brd.ajx; UYAP'ın kodu yok, tekrarlar da reddedilince
        // takipAcilisEvraklariniGonder bu sentetik kodu verir (bkz. takipTalebiIcerikRetHatasi).
        errorCodes: ['ICRAPRO_TAKIP_TALEBI_ICERIK_RET'], // TAKIP_TALEBI_ICERIK_RET_KODU
        userMessage:
            'UYAP takip talebini kabul etmedi; evrakta değişiklik yok, ret UYAP kaynaklı.\n' +
            'UYAP\'ın cevabı: "Takip Talebi evrağı içeriğinde değişiklik yapmadan imzalayıp göndermeniz gerekmektedir." ' +
            'Takip talebi UYAP\'tan indirilip değiştirilmeden imzalandı; UYAP aynı evrakı bazen reddedip bazen kabul ediyor. ' +
            'Takip açılmadı ve harç ödenmedi; tevzi alınan dosya UYAP\'ta "Tamamlanmayan Dosyalar" bölümünde bekliyor. ' +
            'Hash kaydını silip yeniden denerseniz yeni tevzi alınır; bu durumda eski dosyayı 10 dakika sonra UYAP\'ta iptal ediniz.',
    },
    // ── Yeni yönlendirmeler buraya eklenir ──
    // {
    //     key: 'ORNEK_HATA',
    //     errorCodes: ['PRTL_XXX_00000'],
    //     userMessage: 'Kullanıcıya gösterilecek yönlendirici mesaj...',
    // },
];

/**
 * Bir hatayı sözlükteki kurallara karşı dener; errorCode'u eşleşen ilk
 * yönlendirmeyi döner, yoksa null.
 *
 * UyapRequest iş-mantığı hatalarında reject objesi düz { error, errorCode, message }
 * biçimindedir (HTTP response taşımaz); axios kaynaklı hatalarda ise response.data
 * bulunur. Her iki şekildeki errorCode da kontrol edilir.
 */
export function matchUyapError(e: any): UyapErrorYonlendirme | null {
    const code = e?.errorCode ?? e?.response?.data?.errorCode;
    if (!code) return null;

    for (const rule of UYAP_ERROR_YONLENDIRMELERI) {
        if (rule.errorCodes.includes(code)) {
            return rule;
        }
    }

    return null;
}

/** Tekrarlar da reddedilince takip talebi hatasına verilen sentetik kod (DEV-3880). */
export const TAKIP_TALEBI_ICERIK_RET_KODU = 'ICRAPRO_TAKIP_TALEBI_ICERIK_RET';

function hataMetni(e: any): string {
    if (e != null && typeof e === 'object') {
        if (typeof e.message === 'string' && e.message.length > 0) return e.message;
        if (typeof e.error === 'string' && e.error.length > 0) return e.error;
        return '';
    }
    return e == null ? '' : String(e);
}

/**
 * DEV-3880: UYAP imzalı takip talebini zaman zaman kendi ürettiğiyle eşleştiremiyor ve
 * davaAcilisEvrakGonderme_brd.ajx errorCode'suz şu cevabı veriyor:
 * {"type":"error","message":"Takip Talebi evrağı içeriğinde değişiklik yapmadan imzalayıp göndermeniz gerekmektedir.","log":""}
 * Evrak değiştirilmeden imzalanıyor ve aynı evrak sonraki denemede kabul edilebiliyor; hata
 * metinden tanınır. Vekalet/dayanak için gelebilecek benzer bir mesaj takip talebini yeniden
 * indirmekle düzelmeyeceği için "takip talebi" ifadesi de aranır.
 */
export function takipTalebiIcerikRetMi(e: any): boolean {
    if (e?.errorCode === TAKIP_TALEBI_ICERIK_RET_KODU)
        return false;
    // "İ".toLowerCase() "i̇" verdiği için Türkçe büyük harfler önce elle çevrilir.
    const metin = hataMetni(e).replace(/İ/g, 'i').replace(/I/g, 'ı').toLowerCase();
    return metin.indexOf('takip talebi') >= 0 && metin.indexOf('yapmadan imzalay') >= 0;
}

/** Tekrarlar da reddedilince fırlatılan hata; matchUyapError yönlendirici mesajı bulur. */
export function takipTalebiIcerikRetHatasi(e: any): { type: string, errorCode: string, message: string, uyapMesaji: string } {
    const kural = UYAP_ERROR_YONLENDIRMELERI.find((k) => k.errorCodes.includes(TAKIP_TALEBI_ICERIK_RET_KODU));
    return {
        type: 'error',
        errorCode: TAKIP_TALEBI_ICERIK_RET_KODU,
        message: kural ? kural.userMessage : hataMetni(e),
        uyapMesaji: hataMetni(e)
    };
}
